// Deterministic analysis engine — computes verified facts in code before any LLM sees the data
import { KPI_DEFS, parseNumber, calcChange, CHANNEL_NAME_KEY } from "./utils";

const fmt = (n, dp = 0) => (n === null || n === undefined || isNaN(n) ? "n/a" : n.toLocaleString(undefined, { maximumFractionDigits: dp }));
const pct = (v) => (v === null || v === undefined ? "n/a" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`);

// Revenue bridge: ΔR decomposed into traffic / conversion / AOV contributions (sequential method,
// scaled to the actual revenue delta so contributions always sum to the real change)
const revenueBridge = (cur, prev) => {
  const S0 = parseNumber(prev["Sessions"]), S1 = parseNumber(cur["Sessions"]);
  const C0 = parseNumber(prev["Conversion Rate"]), C1 = parseNumber(cur["Conversion Rate"]);
  const A0 = parseNumber(prev["Avg Order Value"]), A1 = parseNumber(cur["Avg Order Value"]);
  const R0 = parseNumber(prev["Total revenue"]), R1 = parseNumber(cur["Total revenue"]);
  if ([S0, S1, C0, C1, A0, A1, R0, R1].some((v) => v === null)) return null;
  const dTraffic = (S1 - S0) * (C0 / 100) * A0;
  const dCvr = S1 * ((C1 - C0) / 100) * A0;
  const dAov = S1 * (C1 / 100) * (A1 - A0);
  const raw = dTraffic + dCvr + dAov;
  const actual = R1 - R0;
  const scale = raw !== 0 ? actual / raw : 1;
  return { total: actual, traffic: dTraffic * scale, cvr: dCvr * scale, aov: dAov * scale };
};

export const computeFindingsSheet = ({ kpiCurrent, kpiPrevious, kpiLastYear, channelSets, config }) => {
  if (!kpiCurrent) return "";
  const sym = { USD: "$", GBP: "£", EUR: "€" }[config.currency] || "$";
  const lines = [];
  lines.push(`REPORT MONTH: ${config.month} ${config.year}${config.country ? ` — ${config.country}` : ""} (currency: ${config.currency})`);

  // KPI deltas
  lines.push("\nKPI CHANGES:");
  KPI_DEFS.forEach((def) => {
    const cur = parseNumber(kpiCurrent[def.key]);
    if (cur === null) return;
    const mom = kpiPrevious ? calcChange(kpiCurrent[def.key], kpiPrevious[def.key]) : null;
    const yoy = kpiLastYear ? calcChange(kpiCurrent[def.key], kpiLastYear[def.key]) : null;
    const val = def.isPercent ? `${fmt(cur, 2)}%` : def.isCurrency ? `${sym}${fmt(cur)}` : fmt(cur);
    const note = def.lowerIsBetter ? " (lower is better)" : "";
    lines.push(`- ${def.label}: ${val} | MoM ${mom !== null ? pct(mom) : "n/a"} | YoY ${yoy !== null ? pct(yoy) : "n/a"}${note}`);
  });

  // Revenue bridge
  if (kpiPrevious) {
    const bridge = revenueBridge(kpiCurrent, kpiPrevious);
    if (bridge) {
      const part = (v) => `${v >= 0 ? "+" : "-"}${sym}${fmt(Math.abs(v))}`;
      lines.push(`\nREVENUE BRIDGE (drivers of the ${part(bridge.total)} MoM revenue change — verified decomposition):`);
      lines.push(`- Traffic volume contribution: ${part(bridge.traffic)}`);
      lines.push(`- Conversion rate contribution: ${part(bridge.cvr)}`);
      lines.push(`- Average order value contribution: ${part(bridge.aov)}`);
    }
  }

  // Channel contributions to MoM revenue change
  const chCur = channelSets?.current, chPrev = channelSets?.previous;
  if (chCur && chPrev) {
    const contribs = chCur
      .map((r) => {
        const name = (r[CHANNEL_NAME_KEY] || "").trim();
        if (!name) return null;
        const prevRow = chPrev.find((p) => (p[CHANNEL_NAME_KEY] || "").trim().toLowerCase() === name.toLowerCase());
        if (!prevRow) return null;
        const delta = (parseNumber(r["Total revenue"]) || 0) - (parseNumber(prevRow["Total revenue"]) || 0);
        return { name, delta, momPct: calcChange(r["Total revenue"], prevRow["Total revenue"]) };
      })
      .filter(Boolean)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    const totalDelta = contribs.reduce((s, c) => s + c.delta, 0);
    if (contribs.length) {
      lines.push(`\nCHANNEL CONTRIBUTIONS to MoM revenue change (total across channels: ${totalDelta >= 0 ? "+" : "-"}${sym}${fmt(Math.abs(totalDelta))}):`);
      contribs.slice(0, 5).forEach((c) => {
        const share = totalDelta !== 0 ? ` (${((c.delta / totalDelta) * 100).toFixed(0)}% of net change)` : "";
        lines.push(`- ${c.name}: ${c.delta >= 0 ? "+" : "-"}${sym}${fmt(Math.abs(c.delta))} (${c.momPct !== null ? pct(c.momPct) : "n/a"})${share}`);
      });
    }
    // Mix shifts
    const totCur = chCur.reduce((s, r) => s + (parseNumber(r["Total revenue"]) || 0), 0);
    const totPrev = chPrev.reduce((s, r) => s + (parseNumber(r["Total revenue"]) || 0), 0);
    if (totCur && totPrev) {
      const shifts = chCur
        .map((r) => {
          const name = (r[CHANNEL_NAME_KEY] || "").trim();
          const prevRow = chPrev.find((p) => (p[CHANNEL_NAME_KEY] || "").trim().toLowerCase() === name.toLowerCase());
          if (!name || !prevRow) return null;
          const shift = ((parseNumber(r["Total revenue"]) || 0) / totCur - (parseNumber(prevRow["Total revenue"]) || 0) / totPrev) * 100;
          return { name, shift };
        })
        .filter((s) => s && Math.abs(s.shift) >= 0.3)
        .sort((a, b) => Math.abs(b.shift) - Math.abs(a.shift));
      if (shifts.length) {
        lines.push("\nREVENUE MIX SHIFTS (share of revenue, MoM, only material moves):");
        shifts.slice(0, 4).forEach((s) => lines.push(`- ${s.name}: ${s.shift >= 0 ? "+" : ""}${s.shift.toFixed(1)}pt`));
      }
    }
  }

  return lines.join("\n");
};
